import { Sizes, Variants } from '@common/types';
import { Box, Input, Label, Tooltip, TooltipPosition } from '@base-app';
import { yupResolver } from '@hookform/resolvers/yup';
import { FC, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { object } from 'yup';
import {
  ButtonStyled,
  FormFieldStyled,
  FormStyled,
  StyledContainer,
  StyledInfoIcon,
  StyledItemContainer,
  StyledLabelContainer,
} from './PersonalInformationForm.styles';
import { PersonalInformationFormProps, UpdateUserFormValues } from './PersonalInformationForm.types';
import { MY_ACCOUNT_TRANSLATION } from '@modules/my-account/constants';
import { COMMON_TRANSLATION } from '@constants/index';
import { getDisplayNameByPartnerOrganization, isSSOUser } from '@modules/core/utils';
import { useUserFieldsValidation } from '@modules/shared/hooks/useUserFieldsValidation';
import { PartnerOrganization } from '@/modules/core/types';

const PersonalInformationForm: FC<PersonalInformationFormProps> = ({
  currentUser,
  phoneNumber,
  onSaveForm,
  organizationName,
}) => {
  const { t } = useTranslation(MY_ACCOUNT_TRANSLATION);
  const { firstName, lastName, email } = useUserFieldsValidation();
  const [canSaveForm, setCanSaveForm] = useState<boolean>(false);

  const INITIAL_FORM = {
    firstName: currentUser?.profile.firstName,
    lastName: currentUser?.profile.lastName,
    email: currentUser?.profile.email,
  };

  const schema = object({
    firstName,
    lastName,
    email,
  }).required();

  const defaultValues: Partial<UpdateUserFormValues> = INITIAL_FORM;

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty, isValid },
    reset,
  } = useForm<UpdateUserFormValues>({
    defaultValues,
    mode: 'all',
    values: {
      firstName: currentUser?.profile.firstName ?? '',
      lastName: currentUser?.profile.lastName ?? '',
      email: currentUser?.profile.email ?? '',
    },
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
  });

  const handleFormSubmit: SubmitHandler<UpdateUserFormValues> = async (values) => {
    if (currentUser !== null) {
      await onSaveForm({
        ...currentUser,
        profile: {
          ...currentUser.profile,
          firstName: values.firstName,
          lastName: values.lastName,
        },
      });
      reset();
    }
  };

  useEffect(() => {
    if (!organizationName || !currentUser) {
      return;
    }

    const isSSO = isSSOUser(currentUser, organizationName);
    setCanSaveForm(!isSSO);
  }, [currentUser, organizationName]);

  return (
    <FormStyled onSubmit={handleSubmit(handleFormSubmit)}>
      <FormFieldStyled css={{ '@sm': { paddingRight: '$8' } }}>
        <Controller
          name="firstName"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              heap-ignore="true"
              id={field.name}
              name={field.name}
              label={t('first_name', { ns: COMMON_TRANSLATION })}
              fullWidth
              error={!(errors.firstName == null)}
              errorDescription={errors.firstName?.message as string}
              disabled={!canSaveForm}
              testId="my-account-form-input-first-name"
              value={currentUser?.profile?.firstName}
            />
          )}
        />
      </FormFieldStyled>
      <FormFieldStyled>
        <Controller
          name="lastName"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              heap-ignore="true"
              id={field.name}
              name={field.name}
              label={t('last_name', { ns: COMMON_TRANSLATION })}
              fullWidth
              error={!(errors.lastName == null)}
              errorDescription={errors.lastName?.message as string}
              disabled={!canSaveForm}
              testId="my-account-form-input-last-name"
              value={currentUser?.profile?.lastName}
            />
          )}
        />
      </FormFieldStyled>
      <StyledContainer>
        <StyledItemContainer css={{ marginBottom: '$9' }}>
          <StyledLabelContainer>
            <Label>{t('email', { ns: COMMON_TRANSLATION })}</Label>
            <Tooltip
              position={TooltipPosition.TOP}
              textStyles={{ color: '$darkBlue200', fontWeight: '$medium' }}
              text={t('email_tooltip_message')}
            >
              <StyledInfoIcon css={{ verticalAlign: 'middle' }} />
            </Tooltip>
          </StyledLabelContainer>
          <Box heap-ignore="true">{currentUser?.profile?.email}</Box>
        </StyledItemContainer>
        <StyledItemContainer css={{ marginBottom: '$9' }}>
          <StyledLabelContainer>
            <Label>{t('phone_number')}</Label>
            <Tooltip
              position={TooltipPosition.TOP}
              textStyles={{ color: '$darkBlue200', fontWeight: '$medium' }}
              text={t('phone_tooltip_message')}
            >
              <StyledInfoIcon css={{ verticalAlign: 'middle' }} />
            </Tooltip>
          </StyledLabelContainer>
          <Box heap-ignore="true">{phoneNumber}</Box>
        </StyledItemContainer>
        <StyledItemContainer>
          <Label>{t('organization')}</Label>
          {organizationName && getDisplayNameByPartnerOrganization(organizationName as PartnerOrganization)}
        </StyledItemContainer>
        {canSaveForm && (
          <ButtonStyled
            css={{
              alignSelf: 'center',
              marginTop: '$9',
              maxWidth: '280px',
            }}
            data-testid={'my-account-form-button-save-changes'}
            disabled={!(isDirty && isValid)}
            size={Sizes.LARGE}
            type="submit"
            variant={Variants.PRIMARY}
          >
            {t('save_changes')}
          </ButtonStyled>
        )}
      </StyledContainer>
    </FormStyled>
  );
};

export default PersonalInformationForm;
